import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { UserSettings } from 'src/Libs/user/types'
import { useCallback, useEffect, useMemo } from 'react'
import { ApiResult, usePortalMutation, usePortalQuery } from 'src/Libs/api'

type UseUserSettings = {
  isUserSettingsLoading: boolean
  isUserSettingsError: boolean
  isUserSettingsUpdating: boolean
  userSettings: UserSettings
  updateUserSettings: (updatedUserSettings: UserSettings) => Promise<void>
}

/**
 * Fetches and updates user settings
 */
const useUserSettings = (): UseUserSettings => {
  const authFetch = useAuthFetchWithResult()
  const userId = useTypedSelector(state => state.auth.userId)
  const refresh = useTypedSelector(state => state.apiView.refresh)

  const {
    data: userSettings,
    error,
    refetch: refetchUserSettings,
    isLoading: isUserSettingsLoading,
    isError: isUserSettingsError,
  } = usePortalQuery({
    queryKey: ['user-settings', userId, refresh],
    queryFn: async () => {
      const res = await authFetch<UserSettings>({
        path: `users/${userId}/settings`,
      })

      return res
    },
    enabled: userId !== undefined,
  })

  useEffect(() => {
    if (isUserSettingsError) {
      console.error(error)
    }
  }, [isUserSettingsError, error])

  const { mutateAsync: updateUserSettingsMutation, isPending: isUserSettingsUpdating } = usePortalMutation({
    mutationFn: async (updatedUserSettings: UserSettings) => {
      await authFetch<void>({
        path: `users/${userId}/settings`,
        method: 'PUT',
        payload: updatedUserSettings,
      })
      return new ApiResult<void>()
    },
    onSuccess: () => {
      refetchUserSettings()
    },
    onError: error => {
      console.error(error)
    },
  })

  const updateUserSettings = useCallback(
    async (updatedUserSettings: UserSettings): Promise<void> => {
      await updateUserSettingsMutation(updatedUserSettings)
    },
    [updateUserSettingsMutation]
  )

  const userSettingsData = useMemo(() => userSettings || {}, [userSettings])

  return { isUserSettingsLoading, isUserSettingsError, isUserSettingsUpdating, userSettings: userSettingsData, updateUserSettings }
}

export default useUserSettings
